import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Button } from "antd";
import { useEffect } from "react";
import { useProjectsPaged } from "@/common/artemis-operations/projects";
import {
	cardSortToKey,
	keyToSort,
} from "@/common/artemis-operations/use-paged-list";
import type { ProjectStatus } from "@/common/model";
import {
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import {
	ProjectStatusBadge,
	ProjectStatusLabelMap,
} from "@/components/projects/project-badges";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/projects/",
)({
	component: Projects,
});

const labelOptions = (map: Record<string, string>) =>
	Object.entries(map).map(([value, label]) => ({ value, label }));

function Projects() {
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { orgSlug, workspaceId } = Route.useParams();
	const list = useProjectsPaged(workspaceId);
	const { openProjectCreateWizard } = useWizards();

	useEffect(() => {
		setTitle("Projects");
		setActions([
			{
				type: "button",
				id: "refresh",
				icon: <CustomIcon icon={IconRefresh} />,
				onClick: () => list.refetch(),
			},
			{
				type: "button",
				id: "create-project",
				tooltip: "New Project",
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => openProjectCreateWizard(),
			},
		]);
	}, [setTitle, openProjectCreateWizard, setActions, list.refetch]);

	const noFilters = !list.search && !list.filters.status;

	if (!list.loading && !list.isError && list.totalCount === 0 && noFilters) {
		return (
			<BasePageContainer>
				<CustomEmpty description="projects" expanded>
					<Button type="primary" onClick={() => openProjectCreateWizard()}>
						Create Project
					</Button>
				</CustomEmpty>
			</BasePageContainer>
		);
	}

	return (
		<QueryGuard isPending={list.loading} isError={list.isError}>
			<BasePageContainer>
				<CardViewer
					items={list.rows}
					getKey={(p) => p.id}
					server={{
						search: list.search,
						onSearchChange: list.setSearch,
						filterValues: {
							status: list.filters.status,
						},
						onFilterChange: (k, v) => list.setFilter(k, v),
						sortValue: cardSortToKey(list.sort),
						onSortChange: (v) =>
							list.setSort(keyToSort[v] ?? ([] as SortingState)),
						totalCount: list.totalCount,
						hasNextPage: list.hasNextPage,
						hasPreviousPage: list.hasPreviousPage,
						onNextPage: list.onNextPage,
						onPreviousPage: list.onPreviousPage,
						pageSize: 50,
						pageIndex: list.pageIndex,
					}}
					onOpen={(p) =>
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/projects/$projectId",
							params: { orgSlug, workspaceId, projectId: p.id },
						})
					}
					searchText={(p) => `${p.name} ${p.description ?? ""}`}
					searchPlaceholder="Search projects"
					filters={[
						{
							key: "status",
							allLabel: "All statuses",
							options: labelOptions(ProjectStatusLabelMap),
							predicate: (p, v) => p.status === (v as ProjectStatus),
						},
					]}
					sorts={[
						// {
						// 	value: "newest",
						// 	label: "Newest",
						// 	compare: (a, b) => asTime(b.createdAt) - asTime(a.createdAt),
						// },
						// {
						// 	value: "oldest",
						// 	label: "Oldest",
						// 	compare: (a, b) => asTime(a.createdAt) - asTime(b.createdAt),
						// },
						{
							value: "name",
							label: "Name A–Z",
							compare: (a, b) => a.name.localeCompare(b.name),
						},
					]}
					renderCard={(p) => (
						<CardBody
							title={p.name}
							extra={<ProjectStatusBadge status={p.status} />}
							description={
								(p.description as string) ||
								(p.summary as string) ||
								"No description"
							}
						/>
					)}
				/>
			</BasePageContainer>
		</QueryGuard>
	);
}
